<template>
  <b-container fluid>
    <b-row class="justify-content-center">
      <b-col lg="8">
        <div class="display-4 font-weight-bold">Grant Badge</div>

        <!-- Search & Filter -->
        <div class="mt-u6 font-weight-bold mb-3">View submission and feedback</div>

        <BaseFilterStatusSubmission v-model="filterOption" @change="fetchEvidenceSubmission" />

        <div class="mt-u6 d-flex justify-content-between">
          <div class="flex-grow-1 mr-u6" style="max-width: 520px">
            <SearchBar
              v-model="searchKeywords"
              placeholder="Search submission ID here"
              :enable-lower-case="false"
              @search="fetchEvidenceSubmission"
              @reset="reset"
            />
          </div>
        </div>

        <!-- Submission table -->
        <EvidenceSubmissionList
          v-if="!isLoading"
          v-model="currentPage"
          :evidence-submissions="searchResults"
          :total-items="totalItems"
          :per-page="perPage"
          mode="issuer"
        />
        <div v-else class="d-flex justify-content-center mt-5">
          <b-spinner label="Loading..." variant="dark" />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import EvidenceSubmission from '@/models/EvidenceSubmission';
import EvidenceSubmissionList from '@/components/EvidenceSubmission/EvidenceSubmissionList.vue';
import SearchBar from '@/views/LearningUnit/components/SearchBar.vue';
import BaseFilterStatusSubmission from '@/components/base-components/BaseFilterStatusSubmission.vue';
import { debounce, kebabCase } from 'lodash';

export default {
  components: {
    SearchBar,
    EvidenceSubmissionList,
    BaseFilterStatusSubmission
  },

  data() {
    return {
      isLoading: true,
      filterOption: this.$route.query?.status
        ? this.$route.query.status.replaceAll('-', '_').toUpperCase()
        : 'ALL',
      searchKeywords: [],
      evidenceSubmissionIds: [],
      totalItems: 0,
      currentPage: this.$route.query?.page ? parseInt(this.$route.query.page) : 1,
      perPage: 20
    };
  },

  computed: {
    fallbackRoute() {
      return {
        name: 'design-planning/info',
        params: {
          learningUnitId: this.learningUnitId
        },
        query: {
          tab: 'evaluation-assessments'
        }
      };
    },

    searchResults() {
      return this.evidenceSubmissionIds.map((id) =>
        EvidenceSubmission.query().with('learning_unit').where('id', id).first()
      );
    }
  },

  watch: {
    currentPage: {
      immediate: true,
      handler(currentPage, previousPage) {
        if (currentPage) {
          this.fetchEvidenceSubmission({ currentPage });
        }

        if (currentPage != previousPage && previousPage != undefined) {
          this.$router.replace({
            query: { ...this.$route.query, page: currentPage }
          });
        }
      }
    },

    filterOption: {
      immediate: false,
      handler(filterOption) {
        this.$router.replace({
          query: { ...this.$route.query, status: kebabCase(filterOption) }
        });
      }
    }
  },

  created() {
    this.fetchEvidenceSubmission = debounce(this.fetchEvidenceSubmission, 100, {
      leading: true,
      trailing: false
    });
  },

  methods: {
    async fetchEvidenceSubmission({ currentPage = 1 }) {
      this.isLoading = true;
      this.currentPage = currentPage;
      const filter = {
        _start: (this.currentPage - 1) * this.perPage,
        _limit: this.perPage,
        submitted_at_null: false
      };

      if (this.searchKeywords.length && !this.searchKeywords.includes('')) {
        const searchParams = this.searchKeywords.map((keyword) => keyword.toUpperCase());

        filter._where = {
          _or: [{ code_contains: searchParams }, { 'learning_unit.name_contains': searchParams }]
        };
      }

      if (this.filterOption !== 'ALL') {
        filter.status = this.filterOption;
      }

      const populate = ['user', 'issuer', 'assessor', 'learning_unit'];

      if (this.filterOption == 'IN_PROGRESS_BY_ASSESSOR') {
        filter.status = 'REVIEW_PENDING';
        filter.assessor_reviewed_at_null = true;
      }

      if (this.filterOption == 'IN_PROGRESS_BY_ISSUER') {
        filter.status = 'REVIEW_PENDING';
        filter.assessor_reviewed_at_null = false;
        filter.issuer_reviewed_at_null = true;
      }

      /* fetch evidence submission */
      const { evidenceSubmissions, totalItems } = await EvidenceSubmission.api().findWithPagination(
        {
          filter,
          populate
        }
      );

      this.totalItems = totalItems;
      this.evidenceSubmissionIds = evidenceSubmissions.map(({ id }) => id);
      this.isLoading = false;
    },

    reset() {
      this.searchKeywords = [];
      this.fetchEvidenceSubmission({ currentPage: 1 });
    }
  }
};
</script>

<style></style>
